import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Clock,
  PlayCircle,
  Bot,
  Search,
  MapPin,
  Globe,
  Target,
  Wrench,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";
import { VIDEOS } from "@/lib/learningHub";
import { INDUSTRY_TIPS, TIP_GROUPS, TIP_TOPICS } from "@/lib/industryTips";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn";

export const Route = createFileRoute("/learn/")({
  head: () => ({
    meta: [
        { name: "description", content: "Free, no-fluff Colorado marketing education. Guides on local SEO, web design, PPC, and AI search written by a Colorado Springs agency owner." },
      {
        title: "Colorado Marketing Education — Free, No Fluff",
      },
      {
        name: "description",
        content:
          "Free Colorado digital marketing education. Watch and read about SEO, GEO AI, web design, and Google Maps — no sign-up required. Owner-led by Chris Heidlebaugh.",
      },
      {
        property: "og:title",
        content: "Colorado Marketing Education — Free, No Fluff",
      },
      {
        property: "og:description",
        content:
          "We pull back the curtain on SEO, GEO AI, web design, and Google Maps for Colorado businesses.",
      },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Colorado Marketing Education — Free, No Fluff" },
      { name: "twitter:description", content: "Free Colorado digital marketing education. Watch and read about SEO, GEO AI, web design, and Google Maps — no sign-up required. Owner-led by Chris Heidlebaugh." },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}${PATH}` },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Colorado Web Impressions — Learning Hub RSS",
        href: `${SITE_URL}/rss.xml`,
      },
    ],
    scripts: [
      breadcrumbScriptTag([
        { name: "Home", path: "/" },
        { name: "Learn (Blog)" },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "Colorado Digital Marketing Education — Welcome from Chris Heidlebaugh",
          description:
            "An introduction to the Colorado Web Impressions Learn hub — straight talk on SEO, GEO AI, web design, and Google Maps for Colorado businesses.",
          thumbnailUrl: "https://i.ytimg.com/vi/cwGnF-8aVuQ/maxresdefault.jpg",
          uploadDate: "2024-01-01",
          embedUrl: "https://www.youtube.com/embed/cwGnF-8aVuQ",
          contentUrl: "https://www.youtube.com/watch?v=cwGnF-8aVuQ",
          publisher: {
            "@type": "Organization",
            name: "Colorado Web Impressions",
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/logo.png`,
            },
          },
        }),
      },
    ],
  }),
  component: LearnHubPage,
});

// ---------- Categories + content registry ----------
const CATEGORIES = [
  { key: "All", icon: LayoutGrid },
  { key: "SEO", icon: Search },
  { key: "GEO AI", icon: Bot },
  { key: "Web Design", icon: Globe },
  { key: "Google Maps", icon: MapPin },
  { key: "PPC", icon: Target },
  { key: "Industry Tips", icon: Wrench },
] as const;

type CategoryKey = (typeof CATEGORIES)[number]["key"];

type ArticleCard = {
  to: string;
  category: Exclude<CategoryKey, "All">;
  readTime: string;
  title: string;
  desc: string;
};

const FEATURED: ArticleCard = {
  to: "/learn/what-is-geo-ai",
  category: "GEO AI",
  readTime: "8 min read",
  title: "What is GEO AI? The Complete Guide for Colorado Businesses",
  desc: "ChatGPT, Google Gemini, and Perplexity are changing how customers find businesses. Here's what GEO AI is, why it matters for Colorado companies, and exactly how we optimize for it.",
};

const ARTICLES: ArticleCard[] = [
  {
    to: "/learn/google-maps-ranking-colorado",
    category: "Google Maps",
    readTime: "6 min read",
    title: "How Google Maps Ranking Works in Colorado (2026 Guide)",
    desc: "The 3 signals that move you into the Map Pack — and the myths that waste your time and money.",
  },
  {
    to: "/learn/seo-pricing-colorado",
    category: "SEO",
    readTime: "5 min read",
    title: "How Much Does SEO Cost in Colorado? Real Prices, No Fluff",
    desc: "What you should actually pay for SEO in Colorado Springs and Denver — and the red flags that mean you're overpaying.",
  },
  {
    to: "/learn/geo-ai-vs-seo",
    category: "GEO AI",
    readTime: "5 min read",
    title: "GEO AI vs. Traditional SEO: What's the Difference?",
    desc: "Why ranking on Google isn't enough anymore — and how AI search is changing what Colorado businesses need to do.",
  },
  {
    to: "/learn/web-design-cost-colorado",
    category: "Web Design",
    readTime: "7 min read",
    title: "What Should a Business Website Cost in Colorado?",
    desc: "A no-BS breakdown of what a quality website costs in 2026 — and what you get at each price point.",
  },
  {
    to: "/learn/hvac-marketing-colorado",
    category: "Industry Tips",
    readTime: "6 min read",
    title: "HVAC Marketing in Colorado: What Actually Gets You Leads",
    desc: "Google Ads, SEO, or Google Maps? We break down the exact marketing mix that fills Colorado HVAC schedules year-round.",
  },
  {
    to: "/learn/google-ads-vs-seo-colorado",
    category: "PPC",
    readTime: "5 min read",
    title:
      "Google Ads vs. SEO: Where Should Colorado Businesses Spend First?",
    desc: "Which channel makes sense for your stage of business — explained simply, without the agency spin.",
  },
];

// ---------- Starter guides for new owners ----------
const STARTER_GUIDES: { to: string; title: string; desc: string }[] = [
  { to: "/colorado-springs-headquarters", title: "Colorado Springs Headquarters", desc: "Our home office and the 60-mile service area we cover across El Paso, Teller, Douglas, Pueblo and Fremont counties." },
  { to: "/dental-marketing-colorado", title: "Dental Marketing Services", desc: "Patient acquisition tips, a 90-day local SEO plan and a quote form for Colorado dental practices." },
  { to: "/med-spa-marketing-colorado", title: "Med Spa Marketing Services", desc: "Treatment booking tips, a 90-day local SEO plan and a quote form for Colorado med spas." },
  { to: "/law-firm-marketing-colorado", title: "Law Firm Marketing Services", desc: "Case inquiry tips, a 90-day local SEO plan and a quote form for Colorado law firms." },
  { to: "/junk-removal-marketing-colorado", title: "Junk Removal Marketing Services", desc: "Job booking tips, a 90-day local SEO plan and a quote form for Colorado junk haulers." },
  { to: "/digital-advertising-ppc-colorado", title: "Digital Advertising & PPC Management", desc: "Paid search and social ads for Colorado businesses — ad spend goes straight to the platforms, no agency markup." },
  { to: "/colorado-marketing-seo-web-design-costs", title: "Colorado SEO & Web Design Cost Guide", desc: "Real 2026 prices for websites, SEO, Google Maps, AI search and ads — no contracts, no ad markup." },
  {
    to: "/learn/angi-thumbtack-alternatives-colorado",
    title: "Angi & Thumbtack Alternatives",
    desc: "A fair look at lead services and the free, owned channels Colorado contractors can add alongside them.",
  },
  {
    to: "/learn/first-time-website-owner-guide",
    title: "First-Time Website Owner Guide",
    desc: "Never owned a website? Start here — domains, hosting, content, and what to expect month one.",
  },
  {
    to: "/learn/new-business-website-checklist",
    title: "New Business Website Checklist",
    desc: "The exact launch checklist we use for new Colorado businesses — nothing skipped, nothing fluff.",
  },
  {
    to: "/learn/franchise-location-website-guide",
    title: "Franchise Location Website Guide",
    desc: "Opening a franchise location? How to handle brand rules, local SEO, and corporate web restrictions.",
  },
  {
    to: "/learn/buying-an-existing-business-website",
    title: "Buying an Existing Business Website",
    desc: "Bought a business? What to audit, what to keep, and what to rebuild before you lose rankings.",
  },
];

// ---------- Platform comparisons by industry ----------
const COMPARISON_INDUSTRIES: { slug: string; label: string }[] = [
  { slug: "hvac", label: "HVAC" },
  { slug: "plumber", label: "Plumber" },
  { slug: "roofing", label: "Roofing" },
  { slug: "electrician", label: "Electrician" },
  { slug: "landscaping", label: "Landscaping" },
  { slug: "dentist", label: "Dentist" },
  { slug: "chiropractor", label: "Chiropractor" },
  { slug: "law-firm", label: "Law Firm" },
  { slug: "med-spa", label: "Med Spa" },
  { slug: "real-estate-agent", label: "Real Estate Agent" },
];
const PLATFORMS = [
  { key: "wix", label: "Wix" },
  { key: "squarespace", label: "Squarespace" },
  { key: "godaddy", label: "GoDaddy" },
] as const;

// ---------- Page ----------
function FilterPills<T extends string>({ label, options, value, onChange }: { label: string; options: readonly T[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = o === value;
        return (
          <button
            key={o}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o)}
            className={`rounded-full border px-4 py-2 text-sm font-bold transition-spring ${
              on
                ? "border-brand-gold bg-brand-gold-deep text-background shadow-card"
                : "border-border bg-card text-foreground hover:border-brand-gold/60"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function IndustryTipsSection() {
  const topics = ["All Topics", ...TIP_TOPICS] as const;
  const groups = ["All Industries", ...TIP_GROUPS] as const;
  const [topic, setTopic] = useState<(typeof topics)[number]>("All Topics");
  const [group, setGroup] = useState<(typeof groups)[number]>("All Industries");
  const list = INDUSTRY_TIPS.filter((t) => group === "All Industries" || t.group === group);
  return (
    <section className="border-t border-border bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">Tips by Industry</span>
          <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
            Marketing tips for <span className="text-gradient-gold">your Colorado business</span>
          </h2>
          <p className="mt-3 text-muted-foreground">Filter by topic and industry to find tips that fit you.</p>
        </div>
        <div className="mt-8 space-y-3">
          <FilterPills label="Filter by topic" options={topics} value={topic} onChange={setTopic} />
          <FilterPills label="Filter by industry" options={groups} value={group} onChange={setGroup} />
        </div>
        <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((t) => (
            <li key={t.slug}>
              <Link
                to="/learn/tips/$slug"
                params={{ slug: t.slug }}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <span className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">{t.industry}</span>
                <h3 className="mt-2 text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">{t.title}</h3>
                {topic === "All Topics" ? (
                  <p className="mt-3 flex-1 text-sm text-muted-foreground">{t.desc}</p>
                ) : (
                  <ul className="mt-3 flex-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {t.tips[topic].slice(0, 2).map((x) => <li key={x}>{x}</li>)}
                  </ul>
                )}
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                  Read tips <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LearnHubPage() {
  const [active, setActive] = useState<CategoryKey>("All");

  const filtered =
    active === "All"
      ? ARTICLES
      : ARTICLES.filter((a) => a.category === active);

  // Featured shows when "All" or its own category is active
  const showFeatured = active === "All" || active === FEATURED.category;

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Marketing Education"
        title={
          <>
            Colorado Marketing Education —{" "}
            <span className="text-gradient-gold">Free, No Fluff</span>
          </>
        }
        description="We pull back the curtain on SEO, GEO AI, web design, and Google Maps. Watch, read, and decide if we're the real deal — no sign-up required."
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Learn (Blog)" }]} />
        </div>
      </PageHero>

      {/* Featured intro video — compact */}
      <section className="bg-background pt-10 lg:pt-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="mb-4 flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-widest text-brand-gold-deep">
            <PlayCircle className="h-3.5 w-3.5" />
            Start here · 2 min intro
          </div>
          <div className="group relative overflow-hidden rounded-xl border border-border bg-card shadow-card transition-spring hover:border-brand-gold/40 hover:shadow-gold">
            <div className="absolute inset-x-0 top-0 z-10 h-0.5 bg-gradient-gold" />
            <div className="relative aspect-video w-full">
              <iframe
                src="https://www.youtube-nocookie.com/embed/cwGnF-8aVuQ?rel=0&modestbranding=1"
                title="Welcome to the Colorado Web Impressions Learn hub"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category pills */}
      <section className="border-b border-border bg-background py-8">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div
            role="tablist"
            aria-label="Filter articles by category"
            className="flex flex-wrap gap-2"
          >
            {CATEGORIES.map((c) => {
              const isActive = active === c.key;
              const Icon: LucideIcon = c.icon;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(c.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-bold transition-spring ${
                    isActive
                      ? "border-brand-gold bg-brand-gold-deep text-background shadow-card"
                      : "border-border bg-card text-foreground/80 hover:border-brand-gold/50 hover:text-brand-gold-deep"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {c.key}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured + grid */}
      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {showFeatured && <FeaturedCard article={FEATURED} />}

          {filtered.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-border bg-card p-10 text-center shadow-card">
              <p className="text-base text-muted-foreground">
                No articles in this category yet — check back soon.
              </p>
            </div>
          ) : (
            <ul
              className={`grid gap-6 sm:grid-cols-2 lg:grid-cols-3 ${
                showFeatured ? "mt-10" : ""
              }`}
            >
              {filtered.map((a) => (
                <li key={a.to}>
                  <ArticleGridCard article={a} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <IndustryTipsSection />

      {/* Videos section (preserved from existing learning hub) */}
      {VIDEOS.length > 0 && (
        <section className="border-t border-border bg-section-alt py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                <PlayCircle className="h-4 w-4" /> Watch & Learn
              </span>
              <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
                Free Colorado marketing{" "}
                <span className="text-gradient-gold">video lessons</span>
              </h2>
              <p className="mt-3 text-muted-foreground">
                Short, no-fluff videos from Chris on SEO, Google Maps, GEO
                AI, and web design.
              </p>
            </div>

            <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {VIDEOS.slice(0, 6).map((v) => (
                <li key={v.slug}>
                  <a
                    href={`/learn/videos/${v.slug}`}
                    className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
                  >
                    <div className="relative aspect-video w-full overflow-hidden">
                      <img
                        src={`https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`}
                        alt={v.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-spring group-hover:scale-105"
                      />
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"
                      />
                      <div className="pointer-events-none absolute inset-0 grid place-items-center">
                        <span className="grid h-14 w-14 place-items-center rounded-full bg-[#ff0033]/95 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)] ring-2 ring-white/30 transition-spring group-hover:scale-110">
                          <PlayCircle className="h-8 w-8 fill-white text-white" strokeWidth={0} />
                        </span>
                      </div>
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-foreground backdrop-blur">
                        {v.category}
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                        {v.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                        {v.desc}
                      </p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Starter guides for new owners */}
      <section className="border-t border-border bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              <Wrench className="h-4 w-4" /> Starter Guides
            </span>
            <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
              Guides for first-time &amp;{" "}
              <span className="text-gradient-gold">new business owners</span>
            </h2>
            <p className="mt-3 text-muted-foreground">
              Buying a business, opening a franchise, or launching your first
              website? Start with these.
            </p>
          </div>
          <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {STARTER_GUIDES.map((g) => (
              <li key={g.to}>
                <Link
                  to={g.to}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
                >
                  <h3 className="text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                    {g.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {g.desc}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                    Read guide
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Platform comparisons by industry */}
      <section className="border-t border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              <LayoutGrid className="h-4 w-4" /> Platform Comparisons
            </span>
            <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
              Wix, Squarespace &amp; GoDaddy{" "}
              <span className="text-gradient-gold">vs a custom website</span>
            </h2>
            <p className="mt-3 text-muted-foreground">
              Honest, side-by-side comparisons of the DIY platforms vs a
              custom-built site — by industry. We compare platform
              capabilities, not other agencies.
            </p>
          </div>
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {COMPARISON_INDUSTRIES.map((ind) => (
              <li
                key={ind.slug}
                className="rounded-2xl border border-border bg-card p-5 shadow-card"
              >
                <h3 className="text-base font-extrabold text-foreground">
                  {ind.label}
                </h3>
                <ul className="mt-3 space-y-1.5">
                  {PLATFORMS.map((p) => (
                    <li key={p.key}>
                      <Link
                        to={`/${p.key}-vs-custom-website-colorado-${ind.slug}` as string}
                        className="group inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/80 hover:text-brand-gold-deep"
                      >
                        <ArrowRight className="h-3.5 w-3.5 text-brand-gold-deep transition-transform group-hover:translate-x-0.5" />
                        {p.label} vs custom website
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </SiteLayout>
  );
}

// ---------- Cards ----------
function FeaturedCard({ article }: { article: ArticleCard }) {
  return (
    <Link
      to={article.to}
      className="group block overflow-hidden rounded-2xl border border-brand-gold/30 bg-gradient-to-br from-brand-gold/10 via-brand-gold/5 to-transparent p-1 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold hover:shadow-card-hover"
    >
      <div className="rounded-xl bg-card p-8 sm:p-10 lg:p-12">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
            Featured
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-bold text-foreground">
            <Bot className="h-3 w-3" /> {article.category}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
            <Clock className="h-3 w-3" /> {article.readTime}
          </span>
        </div>
        <h2 className="mt-5 text-3xl font-black leading-tight text-foreground sm:text-4xl lg:text-5xl">
          {article.title}
        </h2>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {article.desc}
        </p>
        <span className="mt-6 inline-flex items-center gap-2 text-base font-bold text-brand-gold-deep">
          Read the guide
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function ArticleGridCard({ article }: { article: ArticleCard }) {
  return (
    <Link
      to={article.to}
      className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-block rounded-full bg-brand-gold/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-gold-deep">
          {article.category}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
          <Clock className="h-3 w-3" /> {article.readTime}
        </span>
      </div>
      <h3 className="mt-4 text-lg font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
        {article.title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
        {article.desc}
      </p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
        Read article
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}
